import React, { memo, useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormData } from '../types/questionnaire';
import { 
  FileText, 
  Calculator, 
  Download, 
  Upload, 
  Settings, 
  CheckCircle,
  AlertCircle,
  Clock,
  Trash2,
  X
} from 'lucide-react';

interface DashboardProps {
  data: FormData | null;
  onStartNew: () => void;
  onImportData?: (data: FormData) => void;
}

const Dashboard: React.FC<DashboardProps> = memo(({ data, onStartNew, onImportData }) => {
  const navigate = useNavigate();
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [importStatus, setImportStatus] = useState<{ type: 'success' | 'error' | null; message: string }>({ type: null, message: '' });
  const [importedData, setImportedData] = useState<FormData | null>(null);
  
  const completionStatus = useMemo(() => {
    if (!data) {
      return { 
        status: 'incomplete', 
        message: 'No questionnaire data found. Start a new questionnaire to begin.' 
      };
    }
    
    const requiredFields = [
      'fullName', 'ssn', 'dateOfBirth', 'citizenshipStatus', 'maritalStatus'
    ];
    
    const completedFields = requiredFields.filter(field => data[field as keyof FormData]);
    const completionRate = (completedFields.length / requiredFields.length) * 100;
    
    if (completionRate === 100) {
      return { status: 'complete', message: 'All required information provided' };
    } else if (completionRate > 50) {
      return { status: 'partial', message: `${Math.round(completionRate)}% complete` };
    } else {
      return { status: 'incomplete', message: 'Please complete the questionnaire' };
    }
  }, [data]);

  const statusIcon = useMemo(() => {
    switch (completionStatus.status) {
      case 'complete':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'partial':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      default:
        return <AlertCircle className="w-5 h-5 text-red-500" />;
    }
  }, [completionStatus.status]);

  const statusColor = useMemo(() => {
    switch (completionStatus.status) {
      case 'complete':
        return 'text-green-700 bg-green-50 border-green-200';
      case 'partial':
        return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      default:
        return 'text-red-700 bg-red-50 border-red-200';
    }
  }, [completionStatus.status]);

  const handleExport = useCallback(() => {
    if (data) {
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'questionnaire-data.json';
      link.click();
      URL.revokeObjectURL(url);
    }
  }, [data]);

  const handleCalculateTax = useCallback(() => {
    navigate('/tax-calculation');
  }, [navigate]);

  const handleClearQuestionnaire = useCallback(() => {
    // Clear all questionnaire-related data from localStorage
    localStorage.removeItem('questionnaireData');
    localStorage.removeItem('questionnaire_progress');
    
    // Clear any other related keys
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('questionnaire') || key.startsWith('tax_calc'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    // Call onStartNew to reset state
    onStartNew();
    setShowClearConfirm(false);
    
    // Show success message (optional)
    console.log('Questionnaire data cleared successfully');
  }, [onStartNew]);

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Tax Questionnaire Dashboard
        </h1>
        <p className="text-gray-600">
          Manage your tax questionnaire and view your progress.
        </p>
      </div>

      {/* Status Card */}
      <div className={`rounded-lg border p-6 mb-8 ${statusColor}`}>
        <div className="flex items-center space-x-3">
          {statusIcon}
          <div>
            <h3 className="font-semibold">Questionnaire Status</h3>
            <p className="text-sm opacity-90">{completionStatus.message}</p>
          </div>
        </div>
      </div>

      {/* Action Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Tax Rule Extraction */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Upload className="w-8 h-8 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Extract Tax Rules</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Upload PDF tax documents to extract and save tax rules to the database.
          </p>
          <button
            onClick={() => navigate('/extraction')}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Extract Rules
          </button>
        </div>

        {/* View Tax Rules */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="w-8 h-8 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Tax Rules Database</h3>
          </div>
          <p className="text-gray-600 mb-4">
            View and manage all tax rules in the database. Filter by country, year, and category.
          </p>
          <button
            onClick={() => navigate('/rules')}
            className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            View Rules
          </button>
        </div>

        {/* Calculation Records (Audit) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="w-8 h-8 text-amber-600" />
            <h3 className="text-lg font-semibold text-gray-900">Calculation Records</h3>
          </div>
          <p className="text-gray-600 mb-4">
            View and manage audit files for each tax calculation run (human-readable Markdown).
          </p>
          <button
            onClick={() => navigate('/calculation-records')}
            className="w-full px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 transition-colors"
          >
            Manage Records
          </button>
        </div>
        {/* Continue/Start Questionnaire */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <FileText className="w-8 h-8 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">Questionnaire</h3>
          </div>
          <p className="text-gray-600 mb-4">
            {completionStatus.status === 'complete' 
              ? 'Questionnaire completed. You can review or start a new one.'
              : 'Complete your tax questionnaire to get started.'
            }
          </p>
          <button
            onClick={onStartNew}
            className="w-full px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            {!data ? 'Start Questionnaire' : completionStatus.status === 'complete' ? 'Start New Questionnaire' : 'Continue Questionnaire'}
          </button>
        </div>

        {/* Tax Calculation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Calculator className="w-8 h-8 text-green-600" />
            <h3 className="text-lg font-semibold text-gray-900">Tax Calculation</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Calculate your tax liability based on your questionnaire responses.
          </p>
          <button
            onClick={handleCalculateTax}
            disabled={!data}
            className={`w-full px-4 py-2 rounded-md transition-colors ${
              data
                ? 'bg-green-600 text-white hover:bg-green-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Calculate Tax
          </button>
        </div>

        {/* Export Data */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Download className="w-8 h-8 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Export Data</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Download your questionnaire data for your records.
          </p>
          <button
            disabled={!data}
            className={`w-full px-4 py-2 rounded-md transition-colors ${
              data
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleExport}
          >
            Export JSON
          </button>
        </div>

        {/* Import Data */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Upload className="w-8 h-8 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">Import Data</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Import previously saved questionnaire data and calculate tax.
          </p>
          
          {/* Import Status Messages */}
          {importStatus.type && (
            <div className={`mb-4 p-3 rounded-md ${
              importStatus.type === 'success' 
                ? 'bg-green-50 border border-green-200 text-green-800' 
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              <div className="flex items-center space-x-2">
                {importStatus.type === 'success' ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <AlertCircle className="w-5 h-5" />
                )}
                <span className="text-sm font-medium">{importStatus.message}</span>
              </div>
            </div>
          )}

          <input
            type="file"
            accept=".json"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                  try {
                    const fileContent = event.target?.result as string;
                    let importedData: FormData;
                    
                    // Try to parse as JSON
                    const parsed = JSON.parse(fileContent);
                    
                    // Handle different JSON structures
                    if (parsed.formData) {
                      // Structure: { formData: {...} }
                      importedData = parsed.formData;
                    } else if (parsed.state?.formData) {
                      // Structure: { state: { formData: {...} } }
                      importedData = parsed.state.formData;
                    } else {
                      // Direct FormData structure
                      importedData = parsed;
                    }
                    
                    // Validate that it looks like questionnaire data
                    if (!importedData || typeof importedData !== 'object') {
                      throw new Error('Invalid questionnaire data format');
                    }
                    
                    // Save imported data
                    setImportedData(importedData);
                    localStorage.setItem('questionnaireData', JSON.stringify(importedData));
                    
                    // Call callback if provided
                    if (onImportData) {
                      onImportData(importedData);
                    }
                    
                    setImportStatus({
                      type: 'success',
                      message: `Successfully imported questionnaire data! ${Object.keys(importedData).length} fields loaded.`
                    });
                    
                    // Clear status after 5 seconds
                    setTimeout(() => setImportStatus({ type: null, message: '' }), 5000);
                    
                  } catch (error) {
                    console.error('Error importing data:', error);
                    setImportStatus({
                      type: 'error',
                      message: `Failed to import data: ${error instanceof Error ? error.message : 'Invalid JSON file'}`
                    });
                    setImportedData(null);
                    
                    // Clear error after 5 seconds
                    setTimeout(() => setImportStatus({ type: null, message: '' }), 5000);
                  }
                };
                reader.readAsText(file);
                
                // Reset file input
                e.target.value = '';
              }
            }}
            className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm mb-4 cursor-pointer hover:border-purple-400 transition-colors"
          />

          {/* Calculate Tax Button - Shows after successful import */}
          {(importedData || data) && (
            <button
              onClick={handleCalculateTax}
              className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
            >
              <Calculator className="w-5 h-5" />
              <span>Calculate Tax</span>
            </button>
          )}
        </div>

        {/* Settings */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Settings className="w-8 h-8 text-gray-600" />
            <h3 className="text-lg font-semibold text-gray-900">Settings</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Configure your questionnaire preferences and tax year.
          </p>
          <button className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors">
            Open Settings
          </button>
        </div>

        {/* Clear Questionnaire */}
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
          <div className="flex items-center space-x-3 mb-4">
            <Trash2 className="w-8 h-8 text-red-600" />
            <h3 className="text-lg font-semibold text-gray-900">Clear Questionnaire</h3>
          </div>
          <p className="text-gray-600 mb-4">
            Permanently delete all questionnaire data and start fresh. This action cannot be undone.
          </p>
          <button
            onClick={() => setShowClearConfirm(true)}
            disabled={!data}
            className={`w-full px-4 py-2 rounded-md transition-colors ${
              data
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Clear All Data
          </button>
        </div>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <Trash2 className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Clear Questionnaire Data?
                  </h3>
                </div>
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to permanently delete all questionnaire data? This will remove:
              </p>
              
              <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
                <li>All questionnaire answers</li>
                <li>Saved progress</li>
                <li>Completed questionnaire data</li>
              </ul>
              
              <p className="text-red-600 font-medium mb-6">
                This action cannot be undone.
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowClearConfirm(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearQuestionnaire}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Yes, Clear All Data
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Data Summary */}
      {data && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Personal Information</h4>
              <p className="text-sm text-gray-600">
                {data.fullName ? `Name: ${data.fullName}` : 'Not provided'}
              </p>
              <p className="text-sm text-gray-600">
                {data.citizenshipStatus ? `Status: ${data.citizenshipStatus}` : 'Not provided'}
              </p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Filing Status</h4>
              <p className="text-sm text-gray-600">
                {data.maritalStatus ? `Marital: ${data.maritalStatus}` : 'Not provided'}
              </p>
              <p className="text-sm text-gray-600">
                {data.hasDependents ? `Dependents: ${data.numberOfDependents || 0}` : 'No dependents'}
              </p>
            </div>
            
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Income Sources</h4>
              <p className="text-sm text-gray-600">
                {data.incomeSources?.length || 0} income sources selected
              </p>
              <p className="text-sm text-gray-600">
                {data.livingOutsideUS ? 'Living abroad' : 'Living in US'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

Dashboard.displayName = 'Dashboard';

export default Dashboard;
